import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { TimePicker } from 'baseui/timepicker';
import { LabelSmall } from 'baseui/typography';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createFleetAssetRefuel } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { PaymentMethod } from '../../../types/app';
import { FuelType } from '../../../types/asset';
import { FleetAssetRefuelRequest } from '../../../types/fleet';
import { preventMinus } from '../../../utils/formUtilis';
import LocalizedDatePicker from '../../shared/LocalizedDatePicker';
import PaymentMethodSelect from '../../shared/PaymentMethodSelect';
import FleetAssetFuelTypeSelect from '../shared/FleetAssetFuelTypeSelect';

type Props = Readonly<{
  afterSave: () => void;
  assetId: string;
  onCancel: () => void;
}>;

function FleetAssetRefuelForm({ assetId, onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [editingRefuel, setEditingRefuel] = useState<FleetAssetRefuelRequest>({
    gasStation: '',
    liters: 0,
    paymentMethod: PaymentMethod.credit,
    reference: '',
    cost: 0,
    date: Date.now(),
    type: FuelType.magna,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  const canSubmit = useMemo(() => {
    return (
      !!editingRefuel.gasStation &&
      !!editingRefuel.paymentMethod &&
      !!editingRefuel.reference &&
      !!editingRefuel.date &&
      !!editingRefuel.type &&
      editingRefuel.cost >= 0 &&
      editingRefuel.liters >= 0
    );
  }, [
    editingRefuel.cost,
    editingRefuel.date,
    editingRefuel.gasStation,
    editingRefuel.liters,
    editingRefuel.paymentMethod,
    editingRefuel.reference,
    editingRefuel.type,
  ]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetAssetRefuelRequest) => {
    setEditingRefuel((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!apiParams) return;
    startLoading();
    const { id, error } = await createFleetAssetRefuel({
      assetId,
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
      data: editingRefuel,
    });
    const feedbackPrefix = FEEDBACK_PREFIXES.refuel;
    if (!error && id) {
      afterSave();
      showPositiveFeedback(FEEDBACK.registered(feedbackPrefix));
    } else {
      showFailFeedback(FEEDBACK.failedRegistry(feedbackPrefix));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Fecha y hora: *</LabelSmall>
          <FlexRow gap={theme.sizing.scale300}>
            <LocalizedDatePicker
              value={editingRefuel.date ? [new Date(editingRefuel.date)] : null}
              onChange={({ date }) => {
                if (date && !Array.isArray(date)) {
                  onInputChange(date.getTime(), 'date');
                }
              }}
              onClose={() => {
                onInputChange(editingRefuel.date, 'date');
              }}
              size="compact"
              maxDate={new Date()}
              timeSelectStart
            />
            <span className={css({ width: '170px' })}>
              <TimePicker
                size="compact"
                creatable
                value={editingRefuel.date}
                onChange={(time) => {
                  if (time) {
                    onInputChange(time, 'date');
                  }
                }}
              />
            </span>
          </FlexRow>
        </FlexColumn>
        <LabeledInput
          label="Estación:"
          value={editingRefuel.gasStation ?? ''}
          placeholder="Ingresa la estación"
          onChange={(value) => onInputChange(value, 'gasStation')}
          required
        />
        <FleetAssetFuelTypeSelect
          selectedId={editingRefuel.type}
          onChangeId={(fuelTypeId) => {
            onInputChange(fuelTypeId, 'type');
          }}
          required
        />
        <LabeledInput
          label="Litros:"
          value={editingRefuel.liters ?? ''}
          placeholder="Ingresa los litros"
          type="number"
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          onChange={(value) => onInputChange((+value).toString(), 'liters')}
          min={0}
          required
        />
        <LabeledInput
          label="Costo:"
          value={editingRefuel.cost ?? ''}
          placeholder="Ingresa el costo"
          type="number"
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          onChange={(value) => onInputChange((+value).toString(), 'cost')}
          startEnhancer="$"
          endEnhancer="mxn"
          min={0}
          required
        />
        <PaymentMethodSelect
          selectedId={editingRefuel.paymentMethod}
          onChangeId={(paymentMethodId) => {
            onInputChange(paymentMethodId, 'paymentMethod');
          }}
          required
        />
        <LabeledInput
          label="# Tarjeta ó referencia:"
          value={editingRefuel.reference ?? ''}
          placeholder="Ingresa la referencia"
          onChange={(value) => onInputChange(value, 'reference')}
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={onCancel}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}

export default FleetAssetRefuelForm;
